import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "../components/Maps/indices-comparator/sidebyside";

const getBoundBox = (coordinates) => {
  const latLngs = coordinates.map((coord) => L.latLng(coord[1], coord[0])); // Note: [lat, lng]
  const bounds = L.latLngBounds(latLngs);

  return {
    minLat: bounds.getSouth(),
    maxLat: bounds.getNorth(),
    minLon: bounds.getWest(),
    maxLon: bounds.getEast(),
  };
};

export const useMapImageOverlay = (png) => {
  const map = useMap();
  const overlayRef = useRef(null);

  useEffect(() => {
    if (overlayRef.current) {
      map.removeLayer(overlayRef.current);
    }
    if (png) {
      const ndviLayer = L.imageOverlay(png, [
        [png.boundBox.maxLat, png.boundBox.minLon],
        [png.boundBox.minLat, png.boundBox.maxLon],
      ]);

      ndviLayer.addTo(map);
      overlayRef.current = ndviLayer;
    }

    return () => {
      if (overlayRef.current) {
        map.removeLayer(overlayRef.current);
      }
    };
  }, [png, map]);
};

export const useSideBySideImageOverlays = (png1, png2, coordinates) => {
  const map = useMap();

  const indice1Ref = useRef(null);
  const indice2Ref = useRef(null);
  const sideBySideRef = useRef(null);

  useEffect(() => {
    // Clear previous layers and sideBySide control if they exist
    if (indice1Ref.current) {
      map.removeLayer(indice1Ref.current);
    }
    if (indice2Ref.current) {
      map.removeLayer(indice2Ref.current);
    }
    if (sideBySideRef.current) {
      map.removeControl(sideBySideRef.current);
    }

    if (png1 && png2) {
      map.createPane("left");
      map.createPane("right");

      const boundBox = getBoundBox(coordinates);

      // Create new image overlay layers
      const ndviLayer1 = L.imageOverlay(
        png1,
        [
          [boundBox.maxLat, boundBox.minLon],
          [boundBox.minLat, boundBox.maxLon],
        ],
        { pane: "left" }
      );
      indice1Ref.current = ndviLayer1;

      const ndviLayer2 = L.imageOverlay(
        png2,
        [
          [boundBox.maxLat, boundBox.minLon],
          [boundBox.minLat, boundBox.maxLon],
        ],
        { pane: "right" }
      );
      indice2Ref.current = ndviLayer2;

      // Add new layers to the map
      ndviLayer1.addTo(map);
      ndviLayer2.addTo(map);

      // Create new sideBySide control
      const sideBySide = L.control.sideBySide(ndviLayer1, ndviLayer2);
      sideBySide.addTo(map);
      sideBySideRef.current = sideBySide;
    }

    // Clean up function to remove layers and sideBySide control when component unmounts
    return () => {
      if (indice1Ref.current) {
        map.removeLayer(indice1Ref.current);
      }
      if (indice2Ref.current) {
        map.removeLayer(indice2Ref.current);
      }
      if (sideBySideRef.current) {
        map.removeControl(sideBySideRef.current);
      }
    };
  }, [png1, png2, map, coordinates]);
};
